// src/screens/LobbyScreen.js
// Event Lobby - Live ad service integration via adService.js
// Ads served from Firestore ad_creatives, falls back to demo ads
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View, Text, SafeAreaView, ScrollView,
  StyleSheet, TouchableOpacity, TextInput, ActivityIndicator,
  Pressable
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import {
  collection, query, where, onSnapshot, doc,
  getCountFromServer
} from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAds, AdBanner, insertAdsIntoList } from '../services/adService';

import { GAME_PROFILES } from '../constants/gameData';
import { fmtCrits } from '../utils/currencyHelper';
import { describeCalendar } from '../utils/leagueSchedule';
import { useGameAvailability } from '../hooks/useGameAvailability';
import useCritsEnabled from '../hooks/useCritsEnabled';
import {
  getFightingBracketMeta,
  getMobaBracketMeta,
  getRtsBracketMeta,
  getShooterBracketMeta,
  getSportsBracketMeta,
} from '../constants/gameMeta';




/* ------------------ helpers ------------------ */
const cents = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);

const nowMs = () => Date.now();

const getStartMs = (it) => {
  const s = it?.startAt ?? it?.startTime ?? it?.scheduledStartTime;
  if (s?.toMillis) return s.toMillis();
  if (typeof s === 'object' && s?.seconds != null) return s.seconds * 1000;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d.getTime() : 0;
};

// Get registration close time - this is when players can no longer register
const getRegCloseMs = (it) => {
  // Check for explicit registration close time
  const rc = it?.registrationClosesAt ?? it?.registrationCloseTime ?? it?.regClosesAt;
  if (rc?.toMillis) return rc.toMillis();
  if (typeof rc === 'object' && rc?.seconds != null) return rc.seconds * 1000;
  if (rc) {
    const d = new Date(rc);
    if (Number.isFinite(d.getTime())) return d.getTime();
  }
  
  // Fallback: Calculate from start time minus regCloseMinsBefore (default 15 mins)
  const startMs = getStartMs(it);
  if (startMs > 0) {
    const minsBefore = Number(it?.regCloseMinsBefore) || 15;
    return startMs - (minsBefore * 60 * 1000);
  }
  
  return 0;
};

const isLive = (it) => it?.status === 'live';
// Fill-mode ("Open Until Full") events have no startAt/registrationClosesAt
// until capacity is reached. Treat them as open while still 'scheduled' and
// unfilled; rely on filledAt / status to detect close.
const isFillMode = (it) => it?.startMode === 'fill';
const isRegClosed = (it) => {
  if (it?.status === 'live' || it?.status === 'seeding' || it?.status === 'locked' || it?.status === 'cancelled') return true;
  if (isFillMode(it)) return !!it?.filledAt;
  return getRegCloseMs(it) <= nowMs();
};

// Pretty USD formatter (commas + 2 decimals)
const usd = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n / 100);
};

// map event category key
const categoryForEvent = (it) => {
  const explicit = (it?.gameCategory || '').toLowerCase();
  if (explicit) return explicit;
  const t = GAME_PROFILES?.[it.game]?.type || '';
  if (!t) {
    // Open-catalog events (cg_* community slugs, browse-catalog keys) have no
    // GAME_PROFILES type — without a category of their own they could never
    // match a chip and would be invisible in the Lobby.
    return 'community';
  }
  return t === 'shooter' ? 'shooting' : t; // normalize
};

const getCapacityPlayers = (it) => {
  const teamSize = Math.max(1, Number(it.teamSize || 1));
  const entryUnit = it.entryUnit || 'player';

  if (Number.isFinite(Number(it.entriesExpected))) {
    const units = Number(it.entriesExpected);
    return entryUnit === 'player' ? units : units * teamSize;
  }
  if (it.mode === 'bracket' && Number.isFinite(Number(it.bracketSize))) {
    const entrants = Number(it.bracketSize);
    return entryUnit === 'player' ? entrants : entrants * teamSize;
  }
  if (it.mode === 'h2h') return entryUnit === 'player' ? 2 : 2 * teamSize;
  if (Number.isFinite(Number(it.gridSize)) && Number.isFinite(Number(it.heatsCount)))
    return Number(it.gridSize) * Number(it.heatsCount);
  if (Number.isFinite(Number(it.maxEntries))) {
    const entries = Number(it.maxEntries);
    return entryUnit === 'player' ? entries : entries * teamSize;
  }
  return 0;
};

const entrantsToPlayers = (it, entrants) => {
  const teamSize = Math.max(1, Number(it.teamSize || 1));
  const entryUnit = it.entryUnit || 'player';
  return entryUnit === 'player' ? entrants : entrants * teamSize;
};
const entrantsToPayingUnits = (it, entrants) => Math.max(0, Number(entrants || 0));
const playersToPayingUnits = (it, playersCount) => {
  const teamSize = Math.max(1, Number(it.teamSize || 1));
  const entryUnit = it.entryUnit || 'player';
  if (entryUnit === 'player') return Math.max(0, Number(playersCount || 0));
  return Math.max(0, Math.floor(Number(playersCount || 0) / teamSize));
};

// Crit pool paid to finishers: critEntryCost x paying units, minus the 10% burn
// (non-cash inflation sink). Returns WHOLE CRITS, not cents.
const computePayoutCents = (it, payingUnits) => {
  const critCost = Math.max(0, Math.floor(Number(it.critEntryCost || 0)));
  const burnPct = Number.isFinite(it.rakePercent) ? it.rakePercent : 10;
  const gross = critCost * Math.max(0, Number(payingUnits || 0));
  // Round the paid pool UP to the nearest Crit (non-monetary scoreboard).
  return Math.max(0, Math.ceil(gross * (1 - burnPct / 100)));
};

/**
 * Compact platform label for event cards. Platform-locked events (anything
 * other than Cross-Platform) only admit players with an IGN on that platform.
 */
function platformLabel(platform) {
  const p = String(platform || '');
  if (p.toLowerCase() === 'cross-platform') return 'Crossplay';
  if (p.toLowerCase().startsWith('xbox')) return 'Xbox';
  return p;
}

/**
 * Generate marketing title for event listing
 * PUBLISHER COMPLIANCE: Event name must NOT include game name
 * Game name is shown separately via GAME_PROFILES[item.game].displayName
 * 
 * Priority order:
 * 1. Use the stored event.name field (already compliant if created after fix)
 * 2. Fallback to bracketMeta name if no stored name
 * 3. Final fallback to mode-based generic name (NO game name)
 */
function marketingTitleFor(item) {
  // 1. FIRST: Check if the event has a stored name (includes prize amount)
  //    This is what CreateEventsScreen saves with the full name
  if (item?.name) {
    return item.name;
  }

  // 2. FALLBACK: Try bracket meta lookup (for legacy events without stored names)
  const gameKey = item?.game;
  const gp = GAME_PROFILES?.[gameKey] || {};
  const gameType = gp?.type;
  const mode = (item?.mode || '').toLowerCase();

  let meta = null;

  if (mode === 'bracket') {
    const teamCount = Number(item?.bracketSize || 0); // bracketSize = number of teams
    if (teamCount > 0) {
      if (gameType === 'fighting') meta = getFightingBracketMeta(gameKey, teamCount);
      else if (gameType === 'moba') meta = getMobaBracketMeta(gameKey, teamCount);
      else if (gameType === 'rts') meta = getRtsBracketMeta(gameKey, teamCount);
      else if (gameType === 'shooter') meta = getShooterBracketMeta(gameKey, teamCount);
      else if (gameType === 'sports') meta = getSportsBracketMeta(gameKey, teamCount);
    }
  }

  if (meta?.name) return meta.name;

  // 3. FINAL FALLBACK: Generate generic name WITHOUT game name (publisher compliant)
  // Mode-based naming: "Tournament", "H2H Match", "Showdown", etc.
  if (mode === 'h2h') return 'H2H Match';
  if (mode === 'bracket') return 'Tournament';
  if (mode === 'showdown') return 'Showdown';
  if (mode === 'racing') return 'Race';
  return mode ? mode.charAt(0).toUpperCase() + mode.slice(1) : 'Event';
}

const Countdown = ({ targetMs, label = 'Reg Closes' }) => {
  const [left, setLeft] = useState(targetMs - nowMs());
  useEffect(() => {
    const id = setInterval(() => setLeft(targetMs - nowMs()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  if (left <= 0) return <Text style={styles.regClosedIndicator}>{label === 'Reg Closes' ? 'Reg Closed' : 'Live'}</Text>;

  const sec = Math.floor(left / 1000) % 60;
  const min = Math.floor(left / 60000) % 60;
  const hr  = Math.floor(left / 3600000) % 24;
  const day = Math.floor(left / 86400000);

  return (
    <View style={styles.countdownRow}>
      {[['d',day],['h',hr],['m',min],['s',sec]].map(([k,v]) => (
        <Text key={k} style={styles.countdownText}>{String(v).padStart(2,'0')}{k}{' '}</Text>
      ))}
    </View>
  );
};



/* ------------------ main ------------------ */
export default function LobbyScreen({ navigation }) {
  const isFocused = useIsFocused();
  // Evergreen step 2.3: settings/economy.critsEnabled — OFF hides the Crit
  // pool / entry displays and the balance chip. EventCard/LeagueCard are
  // closures below, so they read this directly.
  const critsEnabled = useCritsEnabled();
  const [userData, setUserData] = useState(null);

  // Data - Only upcoming events (Live events now in Live Matches tab)
  const [upcomingTournaments, setUpcomingTournaments] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [leaguesList, setLeaguesList] = useState([]);

  // counts
  const [entryCounts, setEntryCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(false);

  // UI state
  const [queryText, setQueryText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all'); // 'all'|'30m'|'60m'|'today'
  const [minPrize, setMinPrize] = useState('0');
  const [sortBy, setSortBy] = useState('soonest');     // 'soonest'|'biggest'|'filling'
  const [category, setCategory] = useState('featured'); // chips on this screen

  // Game availability - only show categories with enabled games
  const { games: gameAvailability, loading: availabilityLoading, isGameVisible, isEventVisible } = useGameAvailability();

  // Per-mode soft-hide (settings/events.hideHeats / .hideTournaments): when a
  // flag is on, that mode's events vanish from the lobby for everyone except
  // admins. Direct links still work — hidden, not gated.
  const [hiddenModes, setHiddenModes] = useState({ heats: false, tournaments: false, leagues: false });
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'events'), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setHiddenModes({ heats: !!d.hideHeats, tournaments: !!d.hideTournaments, leagues: !!d.hideLeagues });
    }, () => setHiddenModes({ heats: false, tournaments: false, leagues: false }));
    return () => unsub();
  }, []);
  const isAdminViewer = userData?.role === 'admin';
  // An event is hidden from this viewer if its mode's flag is on (admins exempt).
  const isModeHidden = useCallback((it) => {
    if (isAdminViewer) return false;
    const m = String(it?.mode || '').toLowerCase();
    return (m === 'racing' && hiddenModes.heats) || (m === 'bracket' && hiddenModes.tournaments);
  }, [isAdminViewer, hiddenModes]);
  
  // Ad service hook
  const { getNextAd } = useAds('lobby_banner');

  // Compute which categories have at least one enabled game
  const availableCategories = useMemo(() => {
    const categories = new Set();
    
    Object.keys(GAME_PROFILES).forEach(gameKey => {
      // Check if game is enabled in availability config and visible to this
      // viewer (testing = admins only, hidden = nobody)
      const isEnabled = gameAvailability[gameKey]?.enabled !== false;
      if (isEnabled && isGameVisible(gameKey)) {
        const gameType = GAME_PROFILES[gameKey]?.type;
        if (gameType) {
          // Normalize 'shooter' to 'shooting' for display
          categories.add(gameType === 'shooter' ? 'shooting' : gameType);
        }
      }
    });

    // Open catalog: community/browse-catalog events land here.
    categories.add('community');

    return categories;
  }, [gameAvailability, isGameVisible]);

  // Reset to "featured" if current category is no longer available
  useEffect(() => {
    if (category !== 'featured' && !availableCategories.has(category)) {
      setCategory('featured');
    }
  }, [availableCategories, category]);

  /* ----- auth + user ----- */
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) { setUserData(null); return; }
    const unsub = onSnapshot(doc(db, 'users', u.uid), (snap) => {
      setUserData(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [isFocused]);

  /* ----- tournaments (upcoming only - live events in Live Matches tab) ----- */
  useEffect(() => {
    const qUp = query(collection(db, 'tournaments'), where('status', 'in', ['created','scheduled','open','locked','seeded']));
    const unsub = onSnapshot(qUp, (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      items.sort((a,b) => getStartMs(a) - getStartMs(b));
      setUpcomingTournaments(items);
    });
    return () => unsub();
  }, []);

  /* ----- matches H2H (upcoming only - live events in Live Matches tab) ----- */
  useEffect(() => {
    const qUp = query(collection(db, 'matches'), where('status', 'in', ['created','scheduled','open']));
    const unsub = onSnapshot(qUp, (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      items.sort((a,b) => getStartMs(a) - getStartMs(b));
      setUpcomingMatches(items);
    });
    return () => unsub();
  }, []);

  /* ----- leagues (round-robin series; open + in-progress) ----- */
  useEffect(() => {
    const qL = query(collection(db, 'leagues'), where('status', 'in', ['registration', 'active']));
    const unsub = onSnapshot(qL, (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => getStartMs(a) - getStartMs(b));
      setLeaguesList(items);
    }, () => setLeaguesList([]));
    return () => unsub();
  }, []);

  /* ----- load counts ----- */
  useEffect(() => {
    const targets = [
      ...upcomingTournaments.map(t => ({ type:'t', id:t.id })),
      ...upcomingMatches.map(m => ({ type:'m', id:m.id })),
    ];
    if (!targets.length) return;

    let cancelled = false;
    setLoadingCounts(true);
    (async () => {
      const updates = {};
      for (const { type, id } of targets) {
        try {
          const col = collection(db, type === 't' ? 'tournaments' : 'matches', id, 'entries');
          const snap = await getCountFromServer(col);
          updates[`${type}_${id}`] = snap.data().count || 0;
        } catch {
          updates[`${type}_${id}`] = 0;
        }
      }
      if (!cancelled) {
        setEntryCounts(prev => ({ ...prev, ...updates }));
        setLoadingCounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [upcomingTournaments, upcomingMatches]);

  /* ----- "Next reg close" for selected category (tournaments only) ----- */
  const nextRegCloseTs = useMemo(() => {
    const now = Date.now();
    let list = upcomingTournaments.filter(t => getRegCloseMs(t) >= now);
    // Don't count down to an event the viewer can't see (mode hidden, or the
    // game's visibility is testing/hidden)
    list = list.filter(t => !isModeHidden(t) && isEventVisible(t));
    if (category === 'featured') {
      list = list.filter(t => t?.kind === 'official');
    } else {
      list = list.filter(t => categoryForEvent(t) === category);
    }
    const times = list.map(t => getRegCloseMs(t)).filter(Boolean);
    return times.length ? Math.min(...times) : null;
  }, [upcomingTournaments, category, isModeHidden, isEventVisible]);

  /* ----- filtering + decoration ----- */
  const passesFilters = (it, payoutMaxCents) => {
    // Category gate (Featured = admin-created only)
    if (category === 'featured') {
      if (it?.kind !== 'official') return false;
    } else {
      if (categoryForEvent(it) !== category) return false;
    }

    // Text query on title + game name
    const dn = GAME_PROFILES?.[it.game]?.displayName || it.gameName || it.game || '';
    const title = marketingTitleFor(it);
    const q = queryText.trim().toLowerCase();
    if (q && !(`${title} ${dn}`.toLowerCase().includes(q))) return false;

    // Min prize (by max payout). Crits OFF: the chips are hidden, so the
    // filter is a no-op (a stale non-zero pick can't silently empty the list).
    const minPrizeEff = critsEnabled ? minPrize : '0';
    if (Number(minPrizeEff) > 0 && Number(payoutMaxCents) < Number(minPrizeEff)) return false;

    // Time window (all upcoming, no live events here)
    if (timeFilter !== 'all') {
      const ms = getStartMs(it) - nowMs();
      if (timeFilter === '30m' && ms > 30 * 60 * 1000) return false;
      if (timeFilter === '60m' && ms > 60 * 60 * 1000) return false;
      if (timeFilter === 'today') {
        const d1 = new Date();
        const d2 = new Date(getStartMs(it));
        if (d1.toDateString() !== d2.toDateString()) return false;
      }
    }
    return true;
  };

  const decorate = (items, tag) => items.map((it) => {
    const entrants = entryCounts[`${tag}_${it.id}`] ?? 0;
    const playersMax = getCapacityPlayers(it);
    const playersNow = entrantsToPlayers(it, entrants);

    const payingNow = entrantsToPayingUnits(it, entrants);
    const payingMax = playersToPayingUnits(it, playersMax);

    const payoutNow = computePayoutCents(it, payingNow);
    const payoutMax = computePayoutCents(it, payingMax);

    const fill = playersMax > 0 ? playersNow / playersMax : 0;

    return { ...it, _entrants: entrants, _playersNow: playersNow, _playersMax: playersMax, _payoutNow: payoutNow, _payoutMax: payoutMax, _fill: fill };
  });

  const upcomingCombined = useMemo(() => {
    const all = [...decorate(upcomingTournaments, 't'), ...decorate(upcomingMatches, 'm')];
    // League match events live on the LeagueDetail schedule, not the open
    // lobby — they're pre-seeded 2-player events nobody can join.
    return all.filter((it) => !it.leagueId && !isModeHidden(it) && isEventVisible(it));
  }, [upcomingTournaments, upcomingMatches, entryCounts, isModeHidden, isEventVisible]);

  // Leagues follow the same per-game visibility gate as events
  const visibleLeagues = useMemo(
    () => leaguesList.filter(isEventVisible),
    [leaguesList, isEventVisible]
  );

  const filteredUpcoming = useMemo(
    () => upcomingCombined.filter(it => passesFilters(it, it._payoutMax)),
    [upcomingCombined, queryText, minPrize, timeFilter, category, critsEnabled]
  );

  const sorters = {
    soonest: (a, b) => getStartMs(a) - getStartMs(b),
    biggest: (a, b) => b._payoutMax - a._payoutMax,
    filling: (a, b) => b._fill - a._fill,
  };
  const sortedUpcoming = useMemo(() => {
    const arr = [...filteredUpcoming];
    // A 'biggest' pick made while Crits were on must not keep sorting by a
    // pool the OFF UI no longer shows (its chip is hidden, nothing looks lit).
    const sortByEff = (!critsEnabled && sortBy === 'biggest') ? 'soonest' : sortBy;
    arr.sort(sorters[sortByEff] || sorters.soonest);
    return arr;
  }, [filteredUpcoming, sortBy, critsEnabled]);

  // Build list with ads inserted every 3 rows
  const listWithAds = useMemo(() => {
    return insertAdsIntoList(sortedUpcoming, 3);
  }, [sortedUpcoming]);

  /* ----- UI bits ----- */
  const MicroChip = ({ on, label, onPress }) => (
    <TouchableOpacity onPress={onPress} style={[styles.microChip, on && styles.microChipOn]}>
      <Text style={[styles.microChipText, on && styles.microChipTextOn]}>{label}</Text>
    </TouchableOpacity>
  );
  const CategoryChip = ({ val, label }) => (
    <TouchableOpacity onPress={() => setCategory(val)} style={[styles.catChip, category===val && styles.catChipOn]}>
      <Text style={[styles.catChipText, category===val && styles.catChipTextOn]}>{label}</Text>
    </TouchableOpacity>
  );

  const EventCard = ({ item }) => {
    const start = getStartMs(item);
    const title = marketingTitleFor(item);
    const dn = GAME_PROFILES?.[item.game]?.displayName || item.gameName || item.game || 'Game';
    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigation.navigate('EventDetailScreen', {
          id: item.id,
          collection: item.mode === 'h2h' && !item.bracketSize ? 'matches' : 'tournaments'
        })}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle} numberOfLines={1}>{title}</Text>
          <View style={{ flexDirection:'row', gap:6, alignItems:'center' }}>
            <Text style={styles.badgeMini}>{(item.mode || 'event').toUpperCase()}</Text>
            {item.kind === 'official' && <Text style={styles.officialEvent}>Official</Text>}
          </View>
        </View>
        <View style={styles.rowSplit}>
          <View style={{ flexDirection:'row', alignItems:'center', flexShrink:1, marginRight:8 }}>
            <Text style={styles.cardGame} numberOfLines={1}>{dn}</Text>
            {!!item.platform && (
              <Text style={[styles.platformBadge, platformLabel(item.platform) !== 'Crossplay' && styles.platformBadgeLocked]}>
                {platformLabel(item.platform)}
              </Text>
            )}
          </View>
          {critsEnabled && (
          <Text style={styles.potText}>Pool {fmtCrits(item._payoutNow)} / {fmtCrits(item._payoutMax)}</Text>
          )}
        </View>
        <View style={styles.rowSplit}>
          <Text style={styles.cardMeta}>Players {item._playersNow}/{item._playersMax}{critsEnabled ? ` Entry ${fmtCrits(item.critEntryCost)}` : ''}</Text>
          {isLive(item)
            ? <Text style={styles.liveIndicator}>Live</Text>
            : isRegClosed(item)
              ? <Text style={styles.regClosedIndicator}>Reg Closed</Text>
              : isFillMode(item)
                ? <Text style={styles.regOpenIndicator}>Reg Open</Text>
                : <Countdown targetMs={getRegCloseMs(item)} label="Reg Closes" />
          }
        </View>
      </TouchableOpacity>
    );
  };

  // Calendar rhythm ("Tue & Thu at 8:00 PM") for leagues posted with an
  // explicit per-round schedule; older cadence-only docs show nothing extra.
  const leagueRhythm = (lg) => (Array.isArray(lg.roundTimesMs) && lg.roundTimesMs.length > 1
    ? describeCalendar(lg.roundTimesMs) : '');
  const LeagueCard = ({ item }) => {
    const dn = GAME_PROFILES?.[item.game]?.displayName || item.gameName || item.game || 'Game';
    const players = (item.rosterUids || []).length;
    const open = item.status === 'registration';
    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigation.navigate('LeagueDetail', { leagueId: item.id })}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle} numberOfLines={1}>{item.name}</Text>
          <Text style={styles.badgeMini}>LEAGUE</Text>
        </View>
        <View style={styles.rowSplit}>
          <View style={{ flexDirection:'row', alignItems:'center', flexShrink:1, marginRight:8 }}>
            <Text style={styles.cardGame} numberOfLines={1}>{dn}</Text>
            {!!item.platform && (
              <Text style={[styles.platformBadge, platformLabel(item.platform) !== 'Crossplay' && styles.platformBadgeLocked]}>
                {platformLabel(item.platform)}
              </Text>
            )}
          </View>
          {critsEnabled && (
          <Text style={styles.potText}>Pool {fmtCrits(item.potCount || 0)}</Text>
          )}
        </View>
        <View style={styles.rowSplit}>
          <Text style={styles.cardMeta}>
            Players {players}/{item.maxPlayers}{critsEnabled ? `  Entry ${fmtCrits(item.entryCostCrits || 0)}` : ''}  {item.roundFormat === 'race'
              ? `${item.racesCount || item.totalRounds || '—'} ${GAME_PROFILES?.[item.game]?.formats?.[item.formatName]?.battleRoyale ? 'matches · placement points' : 'races · points scoring'}`
              : `Each opponent ${item.meetingsPerOpponent || 1}×`}
            {leagueRhythm(item) ? `  ·  ${leagueRhythm(item)}` : ''}
          </Text>
          {open
            ? <Countdown targetMs={getRegCloseMs(item)} label="Reg Closes" />
            : <Text style={styles.liveIndicator}>Round {item.currentRound || 1}/{item.totalRounds || '—'}</Text>}
        </View>
      </TouchableOpacity>
    );
  };

  const showLeagues = !hiddenModes.leagues || isAdminViewer;

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />

      <ScrollView contentContainerStyle={styles.container}>
        {/* Crit balance + Get Crits CTA (discoverability for memberships/packs) */}
        {userData && critsEnabled && (
          <TouchableOpacity
            style={styles.critChip}
            onPress={() => navigation.navigate('Subscription')}
            activeOpacity={0.85}
          >
            <Ionicons name="flash" size={16} color="#FCD000" style={{ marginRight: 6 }} />
            <Text style={styles.critChipText}>{Number(userData.critBalance || 0).toLocaleString()} Crits</Text>
            <Text style={styles.critChipCta}>Get more →</Text>
          </TouchableOpacity>
        )}

        {/* Search + Filters button */}
        <View style={styles.toolbar}>
          <TextInput
            value={queryText}
            onChangeText={setQueryText}
            placeholder={`Search ${category === 'featured' ? 'Featured' : category.toUpperCase()} events`}
            placeholderTextColor="#FCD000"
            style={styles.searchInput}
          />
          <TouchableOpacity onPress={() => setShowFilters(v => !v)} style={styles.filterBtn}>
            <Ionicons name="options-outline" size={16} color="#FCD000" style={{ marginRight: 4 }} />
            <Text style={styles.filterBtnText}>Filters</Text>
            <Ionicons name={showFilters ? 'chevron-up' : 'chevron-down'} size={14} color="#888" style={{ marginLeft: 4 }} />
          </TouchableOpacity>
        </View>

        {/* Category row + Next start */}
        <View style={styles.catRow}>
          <View style={styles.catChipsWrap}>
            <CategoryChip val="featured" label="Featured" />
            {availableCategories.has('moba') && <CategoryChip val="moba" label="MOBA" />}
            {availableCategories.has('fighting') && <CategoryChip val="fighting" label="Fighting" />}
            {availableCategories.has('shooting') && <CategoryChip val="shooting" label="Shooting" />}
            {availableCategories.has('sports') && <CategoryChip val="sports" label="Sports" />}
            {availableCategories.has('racing') && <CategoryChip val="racing" label="Racing" />}
            {availableCategories.has('rts') && <CategoryChip val="rts" label="RTS" />}
            {availableCategories.has('community') && <CategoryChip val="community" label="More Games" />}
          </View>
          <View style={styles.nextStartBox}>
            <Text style={styles.nextStartLabel}>REG CLOSES</Text>
            {nextRegCloseTs
              ? <Countdown targetMs={nextRegCloseTs} label="Reg Closes" />
              : <Text style={styles.nextStartNone}>--</Text>}
          </View>
        </View>

        {/* Filters drawer */}
        {showFilters && (
          <View style={styles.filtersDrawer}>
            <View style={styles.filtersRow}>
              <Text style={styles.filterLabel}>Time</Text>
              {[
                ['all','All'],
                ['30m','30m'],
                ['60m','60m'],
                ['today','Today'],
              ].map(([val,label]) => (
                <MicroChip key={val} on={timeFilter===val} label={label} onPress={() => setTimeFilter(val)} />
              ))}
            </View>

            {critsEnabled && (
            <View style={styles.filtersRow}>
              <Text style={styles.filterLabel}>Min Prize</Text>
              {['0','50','100','250','500','1000+'].map(v => (
                <MicroChip key={v} on={minPrize===v} label={`${v} Crits`} onPress={() => setMinPrize(v)} />
              ))}
            </View>
            )}

            <View style={styles.filtersRow}>
              <Text style={styles.filterLabel}>Sort</Text>
              {[
                ['soonest','Soonest'],
                ['biggest','Biggest Crit Pool'],
                ['filling','Filling Fast'],
              ].filter(([val]) => critsEnabled || val !== 'biggest').map(([val,label]) => (
                <MicroChip key={val} on={sortBy===val} label={label} onPress={() => setSortBy(val)} />
              ))}
            </View>
          </View>
        )}

        {/* Leagues — round-robin series (soft-hidden via settings/events.hideLeagues) */}
        {showLeagues && (
          <View style={styles.section}>
            <View style={styles.leagueHeaderRow}>
              <Text style={styles.sectionHeader}>Leagues</Text>
              <TouchableOpacity style={styles.startLeagueBtn} onPress={() => navigation.navigate('CreateLeague')}>
                <Ionicons name="add" size={14} color="#000" />
                <Text style={styles.startLeagueBtnText}>Start a League</Text>
              </TouchableOpacity>
            </View>
            {visibleLeagues.length === 0 && (
              <Text style={styles.noEvents}>No open leagues — start the first one</Text>
            )}
            {visibleLeagues.map((item) => <LeagueCard key={item.id} item={item} />)}
          </View>
        )}

        {/* Upcoming section */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Upcoming</Text>
          {listWithAds.length === 0 && (
            <Text style={styles.noEvents}>No upcoming events match your filters</Text>
          )}
          {listWithAds.map((item) => {
            if (item.type === 'ad') {
              const ad = getNextAd();
              if (!ad) return null;
              return <AdBanner key={item.key} ad={ad} placement="lobby_banner" screen="lobby" />;
            }
            return <EventCard key={item.data.id} item={item.data} />;
          })}
        </View>
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

/* ------------------ styles ------------------ */
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#0b0b0b' },
  container: { padding: 12, paddingBottom: 120 },

  // Toolbar
  critChip: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#151515', borderColor: '#FCD000', borderWidth: 1,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 9, marginBottom: 8,
  },
  critChipText: { color: '#FCD000', fontWeight: '800', fontSize: 14 },
  critChipCta: { color: '#A0A0A0', fontSize: 13, fontWeight: '600', marginLeft: 'auto' },

  toolbar: { flexDirection: 'row', gap: 8, alignItems: 'center', marginBottom: 8 },
  searchInput: {
    flex: 1,
    backgroundColor:'#151515', borderColor:'#242424', borderWidth:1,
    borderRadius:10, paddingHorizontal:12, paddingVertical:10, color:'#fff', fontSize:14
  },
  filterBtn: {
    flexDirection:'row', alignItems:'center',
    paddingVertical:10, paddingHorizontal:12, borderRadius:10,
    borderWidth:1, borderColor:'#303030', backgroundColor:'#121212'
  },
  filterBtnText: { color:'#ddd', fontWeight:'800', fontSize:12 },

  // Category chips + Next start
  catRow: { flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginBottom: 6 },
  catChipsWrap: { flexDirection:'row', flexWrap:'wrap', gap:6, flex: 1, paddingRight: 8 },
  catChip: {
    borderWidth:1, borderColor:'#2a2a2a', backgroundColor:'#151515',
    paddingVertical:6, paddingHorizontal:10, borderRadius:10
  },
  catChipOn: { backgroundColor:'#FCD000', borderColor:'#FCD000' },
  catChipText: { color:'#ddd', fontWeight:'800', fontSize:12 },
  catChipTextOn: { color:'#000' },

  nextStartBox: { flexDirection:'row', alignItems:'center', gap: 6 },
  nextStartLabel: { color:'#8a8a8a', fontSize:10, fontWeight:'700' },
  nextStartNone: { color:'#666', fontSize:12, fontWeight:'800' },

  // Filters drawer
  filtersDrawer: { backgroundColor:'#101010', borderColor:'#202020', borderWidth:1, borderRadius:12, padding:10, marginBottom: 6 },
  filtersRow: { flexDirection:'row', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:6 },
  filterLabel: { color:'#9a9a9a', fontSize:11, fontWeight:'800', marginRight:2 },

  microChip: {
    paddingVertical:6, paddingHorizontal:10, borderRadius:999,
    borderWidth:1, borderColor:'#2a2a2a', backgroundColor:'#151515'
  },
  microChipOn: { backgroundColor:'#FCD000', borderColor:'#FCD000' },
  microChipText: { color:'#ddd', fontSize:11, fontWeight:'800' },
  microChipTextOn: { color:'#000' },

  // Sections + list
  section: { marginBottom: 16 },
  leagueHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  startLeagueBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: '#FCD000', borderRadius: 8, paddingVertical: 6, paddingHorizontal: 10,
  },
  startLeagueBtnText: { color: '#000', fontSize: 12, fontWeight: '900' },
  sectionHeader: { color: '#fff', fontSize: 16, fontWeight: '900', marginBottom: 8 },
  noEvents: { color: '#fff', fontSize: 14, fontStyle: 'italic', marginTop: 8, opacity: 0.7 },
  sectionTitle: { color: '#fff', fontSize: 14, fontWeight: '900', marginTop: 10, marginBottom: 6 },
  listCol: { gap: 8 },
  
  // Cards
  card: { width: '100%', backgroundColor: '#0f0f0f', borderRadius: 12, padding: 12, borderWidth: 1, borderColor: '#1a1a1a', marginBottom: 8 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  cardTitle: { color: '#fff', fontSize: 15, fontWeight: '900', flex: 1, marginRight: 10 },
  badgeMini: { color:'#000', backgroundColor:'#FCD000', fontSize:10, fontWeight:'900', paddingHorizontal:6, paddingVertical:2, borderRadius:4, overflow:'hidden' },
  officialEvent: { color:'#FCD000', borderColor:'#FCD000', borderWidth:1, fontSize:10, fontWeight:'900', paddingHorizontal:6, paddingVertical:2, borderRadius:4, overflow:'hidden', marginLeft: 6 },
  rowSplit: { flexDirection:'row', justifyContent:'space-between', alignItems:'center' },
  cardGame: { color: '#cfcfcf', fontSize: 12, opacity: 0.9 },
  platformBadge: {
    color: '#9aa0b4',
    fontSize: 10,
    fontWeight: '700',
    borderWidth: 1,
    borderColor: '#2a2f36',
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
    marginLeft: 6,
    overflow: 'hidden',
  },
  platformBadgeLocked: {
    color: '#4FC3F7',
    borderColor: '#4FC3F766',
  },
  potText: { color:'#FCD000', fontWeight:'900', fontSize: 12, letterSpacing: 0.2 },
  cardMeta: { color: '#9f9f9f', fontSize: 12, marginTop: 4 },
  liveIndicator: { color: '#2ECC71', fontSize: 12, fontWeight: '900' },
  regClosedIndicator: { color: '#e74c3c', fontSize: 12, fontWeight: '900' },
  regOpenIndicator: { color: '#2ECC71', fontSize: 12, fontWeight: '900' },
  countdownRow: { flexDirection: 'row' },
  countdownText: { color: '#FCD000', fontSize: 12, fontWeight: '900' },

  noTournamentsText: { color: '#aaa' },

});